import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "wasp/client/auth";
import { useAction, useQuery } from "wasp/client/operations";
import type { Empty } from "../../types/wasp";
import {
  getUserMealPlans,
  getUsageStatus,
  generateMealPlan,
} from "wasp/client/operations";
import { MyPlansPage } from "../components/MyPlansPage";
import { useNavigate } from "react-router-dom";
import { toV32Plans } from "../adapters/mealPlan";
import {
  ProgressiveSkeleton,
  PROGRESSIVE_SKELETONS,
} from "../components/ui/progressive-skeleton";
import { AnimatedSkeleton } from "../components/ui/animated-skeleton";
import { trackEvent } from "../config/analytics";
import { trace } from "../../utils/trace";
import { toast } from "sonner";
import {
  useAuthenticatedMealPlans,
  selectPlans,
  useOptimisticMealPlanUpdate,
} from "../../stores/MealPlansStore";
import type { MealPlan } from "../../stores/MealPlansStore";

let __plansPageStarted = false;
let __plansPageLogged = false;

/**
 * PlansPage shows the user's plan history + usage metrics.
 * After generating a plan we immediately refetch both the plan list and usage
 * counters so the UI reflects the new quota consumption without a full reload.
 */
export function PlansPage() {
  const { data: user } = useAuth();
  const isAuthenticated = !!user;
  const {
    data: mealPlanHistory,
    isLoading,
    refetch: refetchMealPlanHistory,
  } = useQuery(getUserMealPlans, {}, { enabled: isAuthenticated });
  const { data: usageStatus, refetch: refetchUsage } = useQuery(
    getUsageStatus,
    {},
    { enabled: isAuthenticated },
  );

  const listSkeletons = useMemo(() => PROGRESSIVE_SKELETONS.mealPlanList(6), []);

  // Debug logging for authentication and loading states
  console.log("[PlansPage] Auth state:", { isAuthenticated, user: !!user });
  console.log("[PlansPage] Query states:", {
    mealPlanHistoryLoading: isLoading,
    usageStatus: usageStatus
      ? { hasReachedPlanLimit: usageStatus.hasReachedPlanLimit }
      : null,
  });

  // Add a temporary check to see if user is authenticated
  if (!isAuthenticated) {
    console.warn(
      "[PlansPage] User not authenticated - this may prevent plan creation",
    );
  }

  if (isAuthenticated && !usageStatus) {
    console.warn(
      "[PlansPage] Usage status not loaded yet - this may cause issues",
    );
  }
  const doGeneratePlan = useAction(generateMealPlan);
  const navigate = useNavigate();
  const mealPlansStore = useAuthenticatedMealPlans(selectPlans);
  const { createPlan } = useOptimisticMealPlanUpdate();

  const plans = useMemo(() => {
    // Prefer store data, fallback to query data (supports { items, hasMore } shape)
    const fromQuery = Array.isArray(mealPlanHistory)
      ? mealPlanHistory
      : (mealPlanHistory as any)?.items;
    const sourceData = mealPlansStore || fromQuery;
    if (!sourceData || !Array.isArray(sourceData)) return [];
    return toV32Plans(sourceData);
  }, [mealPlansStore, mealPlanHistory]);

  useEffect(() => {
    if (__plansPageStarted) return; // StrictMode dev guard for duplicate effects
    __plansPageStarted = true;
    trackEvent("plans_view");
    trace("plans_query_start");
  }, []);

  // Watch for store refresh triggers and refetch data
  const lastRefresh = useAuthenticatedMealPlans((state) => state.lastRefresh);

  useEffect(() => {
    if (isAuthenticated && lastRefresh > 0) {
      // Trigger a refresh when the store's lastRefresh changes
      // This ensures UI updates after plan creation
      refetchMealPlanHistory();
    }
  }, [isAuthenticated, lastRefresh, refetchMealPlanHistory]);

  useEffect(() => {
    if (!__plansPageLogged && Array.isArray(plans)) {
      __plansPageLogged = true;
      trace("plans_query_ok", { plans: plans.length });
      trace("plans_loaded");
    }
  }, [plans]);

  const [isGenerating, setIsGenerating] = useState(false);
  const handleGenerateNewPlan = async () => {
    setIsGenerating(true);
    try {
      const newPlan = await doGeneratePlan({});

      toast.success("Meal plan generated!", {
        description: "Your personalized meal plan is ready.",
      });

      // Refresh plans list and usage metrics in the background
      try { await refetchMealPlanHistory(); } catch {}
      try { await refetchUsage(); } catch {}

      navigate(`/plans/${(newPlan as any).mealPlan.id}`, {
        state: { justCreated: true }
      });
    } catch (error: any) {
      toast.error("Failed to generate meal plan", {
        description: error.message || "Please try again later.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const plansProps = {
    onViewPlan: (planId: string) => {
      trackEvent("plan_open", { planId });
      navigate(`/plans/${planId}`);
    },
    onCreateNew: handleGenerateNewPlan,
    currentTier: "starter" as const,
    plans,
    usageStatus,
    isGenerating,
    onRefetchPlans: async () => {
      try { await refetchMealPlanHistory(); } catch {}
      try { await refetchUsage(); } catch {}
    },
  };

  return (
    <>
      {isLoading ? (
        <div className="space-y-6">
          <AnimatedSkeleton variant="shimmer" className="h-8 w-64 rounded-md" />
          <ProgressiveSkeleton
            skeletons={listSkeletons}
            duration={1500}
          />
        </div>
      ) : (
        <MyPlansPage {...plansProps} />
      )}
    </>
  );
}
